/**
 *  Plan store module file (Pinia)
 *
 *  Idiomatic Pinia option store: getters are pure `(state) => value` functions
 *  (see ./getters), and former Vuex mutations are folded into `this`-based
 *  actions (camelCase names). Former Vuex actions are also camelCase.
 *  The ACTIONS/GETTERS/MUTATIONS constant maps are deleted; consumers call
 *  store.actionName() / storeToRefs(store).getterName directly.
 *
 *  Collision note: `SET_ASSETS_TOTAL_STATS` and `SET_PUBLIC_PLANS` existed as
 *  BOTH a Vuex action and a Vuex mutation. Pinia has a single method
 *  namespace; the action was a trivial `commit(...)` wrapper in both cases,
 *  so it collapses 1:1 into the (former) mutation body.
 **/
/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore } from 'pinia'
import { state, MODEL_PLAN, MODEL_ASSETS, MODEL_SUBPLAN_FILTERS } from './state'
import { getters as planGetters } from './getters'

import assetsService from '@/api/assetsService'
import layerServiceC3 from '@/layers/layerService'
import {
  ASSETS,
  CLUSTER_ASSETS,
  EXCLUDED_ASSETS,
  EXCLUDED_CLUSTER_ASSETS,
  HEATMAP_ASSETS_LAYER,
} from '@/layers'
import {
  deserializeFilters,
  deserializeLegend,
} from '@/plan/deserializeFilters'
import {
  filterAssetsFromPlan,
  getUnfilteredElements,
  getPlanFiltersFromStorage,
} from '@/helpers/plan.helper'
import PlanService from '@/services/plan.service'
import CustomGeoboundary from '@/services/custom-geoboundary.service'
import { getAppMetadata } from '@/plan/metadata/metadata'
import { managePoisLayerVisibility } from '@/helpers/pois.helper'
import { manageBoundariesLayerVisibility } from '@/helpers/boundaries.helper'
import { getPOIsByTypesFromFilter } from '@/plan/filter/pois.helper'
import {
  getZoomLevelForDeclustering,
  isAudiencesQuintileEnabled,
  isFeatureLayerHeatMapEnabled,
  isIntelligentMediaTypeDigitalStaticEnabled,
} from '@/plan/metadata/metadata.helper'
import { shouldAssetSimpleLayerBeVisible } from '@/helpers/map.helper'
import { getImpressionsForAssets } from '@/plan/filter/asset.helper'
import sharedPlanService from '@/services/sharedPlan.service'
import BrowserEnvironmentResolver from '@/helpers/environment.helper'
import { serializeLegend } from '@/utils/serializeLegend'
import { getInstance as getAuth0ServiceIntance } from '@/auth/index'
import AssetsService from '@/services/assets-browser.service'
import { sleep } from '@/helpers/common.helper'
import { setupAudiencesQuintileLayer } from '@/helpers/audiences-quintile.helper'
import {
  isAudiencesQuintileInFilter,
  isGeoboundaryFilterReadyForAudiencesQuintile,
} from '@/plan/filter/audiences-quintile.helper'
import { analyzeAssetsForAllDigitalOrAllClassic } from '@/helpers/media-type.helper'
import { prepareLegendInPlan } from '@/helpers/legend.helper'
import { POIsRampColor, POIsOthersColor } from '@/layers/pois/pois-layer.helper'
import { areEquals } from '@/utils/arrayUtils'
import { updatePOIsFilterCache } from '@/layers/pois/pois-filter-cache'
import { deepToRaw } from '@/utils/deepToRaw'

import type { Plan, Poi } from '@workspaces/types'
import {
  DEFAULT_POI_ICON_FIT,
  normalizePoisIcons,
} from '@/helpers/poi-icons.helper'
import type { PlanState, ProximityFlags, SubPlanFilter } from './state.type'
import type { Filter } from '@/plan/filter/filter.type'
import type { DeckLayerLike } from '@/layers/layerService.types'

/**
 * Working shape for a plan being loaded/prepared. It is the MODEL_PLAN shape
 * merged at runtime with the raw fetched plan (which carries Firestore-style
 * timestamps and a possibly-serialized `filters` string), hence the permissive
 * optional fields and index signature.
 */
interface LoadablePlan {
  filters: SubPlanFilter[] | string
  created_at?: { _seconds: number } | number
  modified_at?: { _seconds: number } | number
  poisColors?: string | Record<string, unknown>
  poisIcons?: string | Record<string, unknown>
  legend?: unknown
  [key: string]: unknown
}

interface AssetFeature {
  properties: { excluded?: unknown; id?: unknown; [key: string]: unknown }
  [key: string]: unknown
}

const APP_METADATA = getAppMetadata()
const MODULE_NAME = 'plan'

export const usePlanStore = defineStore(MODULE_NAME, {
  state: (): PlanState => state,
  getters: planGetters,
  actions: {
    // ─── Former mutations folded into this-based actions ────────────────────
    savePlan(payload: any = {}) {
      this.plan = payload
      this.plan_backup = structuredClone(deepToRaw(this.plan))

      const rawPoisColors = payload.poisColors ?? '{}'
      let poisColors = rawPoisColors
      if (typeof rawPoisColors === 'string') {
        poisColors = JSON.parse(rawPoisColors)
      }
      this.poisColors = poisColors

      const rawPoisIcons = payload.poisIcons ?? '{}'
      let poisIcons = rawPoisIcons
      if (typeof rawPoisIcons === 'string') {
        poisIcons = JSON.parse(rawPoisIcons)
      }
      // Plans saved before the fit option store bare basename strings.
      this.poisIcons = normalizePoisIcons(poisIcons)
    },
    setPlanName(payload: any) {
      this.plan.name = payload
    },
    setPlanDescription(payload: any) {
      this.plan.description = payload
    },
    setPlanClient(payload: any) {
      this.plan.client = payload
    },
    setPlanBrand(payload: any) {
      this.plan.brand = payload
    },
    setPlanVisibility(payload: any) {
      this.plan.is_public = payload
    },
    savePlanFilter(
      payload: {
        filters: any[]
        updateDirtyFlags?: boolean
      } = { filters: [], updateDirtyFlags: true },
    ) {
      // Replicate first subfilters to all filters: Proximity, Geographical distribution
      const payloadModified = payload.filters.map((item, index) => {
        if (index !== this.activeSubPlanFiltersIndex) {
          // proximity
          item.proximity = {
            ...payload.filters[this.activeSubPlanFiltersIndex].proximity,
          }
          // geographical distribution
          if (
            this.plan.filters[this.activeSubPlanFiltersIndex]
              .geographical_distribution
          ) {
            item.geographical_distribution = {
              ...payload.filters[this.activeSubPlanFiltersIndex]
                .geographical_distribution,
            }
          }
        }
        return item
      })
      // Set the new filters
      this.plan.filters = payloadModified
      // set custom geoboundaries to the plan
      const customGeoboundariesIds = payload.filters.reduce<string[]>(
        (acc, filter) => {
          const geoboundaries = filter.geoboundaries as
            | Record<string, Array<{ id: string }>>
            | undefined
          if (geoboundaries) {
            const region16Ids = geoboundaries.region_16?.map((r) => r.id) || []
            const region17Ids = geoboundaries.region_17?.map((r) => r.id) || []
            acc = [...acc, ...region16Ids, ...region17Ids]
          }
          return acc
        },
        [],
      )
      const uniqueCustomGeoboundaryIds = [...new Set(customGeoboundariesIds)]
      this.plan.custom_geoboundaries = uniqueCustomGeoboundaryIds || []
      // set custom pois to the plan
      const customPoiIds = payload.filters.reduce<string[]>((acc, filter) => {
        const proximity = filter.proximity
        if (proximity && proximity.data) {
          const data = proximity.data as Array<{ type: string; id: string }>
          const ids =
            data.filter((r) => r.type === 'custom').map((r) => r.id) || []
          acc = [...acc, ...ids]
        }
        return acc
      }, [])
      const uniqueCustomPoiIds = [...new Set(customPoiIds)]
      this.plan.custom_pois = uniqueCustomPoiIds || []

      // Update dirty property
      this.plan.dirty = payload.updateDirtyFlags
        ? payload.updateDirtyFlags
        : true
      // Update plan backup
      this.plan_backup = structuredClone(deepToRaw(this.plan))
    },
    savePlanAidaId(aidaId: string | null) {
      this.plan.aida_id = aidaId
    },
    setAssets(payload: any) {
      this.assets = {
        count: payload.count,
        meta: payload.meta || this.assets.meta,
        data: payload.results || [],
        page: payload.page,
        page_size: payload.page_size,
        loading: false,
      }
    },
    setAssetsExcluded(payload: any) {
      this.assetsExcluded = {
        count: payload.count,
        meta: payload.meta || this.assets.meta,
        data: payload.results || [],
        page: payload.page,
        page_size: payload.page_size,
      }
    },
    setAssetsInitialStats(payload: any) {
      const impressions = payload.allAssetsImpressions ?? -1
      if (!this.assets.meta.stats?.impressions) {
        this.assets.meta = {
          stats: { allAssetsImpressions: impressions, impressions },
        }
      } else {
        this.assets.meta.stats.allAssetsImpressions = impressions
        this.assets.meta.stats.impressions = impressions
        this.plan.impressions = impressions
      }
    },
    // Collision: former ACTIONS.SET_ASSETS_TOTAL_STATS was a trivial
    // `commit(MUTATIONS.SET_ASSETS_TOTAL_STATS, params)` wrapper, so it
    // collapses 1:1 into the mutation body below.
    setAssetsTotalStats(payload: any = {}) {
      this.assets.count = payload.count ?? -1
      if (!this.assets.meta.stats) {
        this.assets.meta.stats = {}
      }
      this.assets.meta.stats.impressions = payload.impressions ?? -1
      this.plan.impressions = payload.impressions ?? -1
    },
    resetPlan() {
      this.plan = MODEL_PLAN()
      this.assets = MODEL_ASSETS()
      // POI icons are plan-scoped (their images live under the plan id in the
      // icons bucket). Loading a plan replaces them via savePlan, but a NEW
      // plan never goes through it, so without clearing here the previous
      // plan's assignments would show up — and be saved — in the new one.
      this.poisIcons = {}
    },
    restorePlanFromBackup() {
      this.plan = structuredClone(deepToRaw(this.plan_backup))
    },
    setOldDirtyPlan() {
      const { filters, previousRoute } = getPlanFiltersFromStorage()
      if (
        previousRoute &&
        previousRoute.includes('/insights') &&
        previousRoute.includes('/panel') &&
        filters !== null
      ) {
        const preparedPlan = { ...this.plan }
        // Stored Filter[] is structurally compatible with SubPlanFilter[].
        preparedPlan.filters = filters as unknown as SubPlanFilter[]
        preparedPlan.dirty = true
        console.debug('🟪 Plan: Restoring an old dirty plan')
        this.plan = preparedPlan
      }
    },
    setAssetsLoading(payload: any) {
      this.assets.loading = payload
    },
    setGeographicalDistributionRegions(payload: any) {
      this.geographicalDistributionRegions = payload || []
    },
    setIsLoadingGeographicalDistributionRegions(payload: any) {
      this.loadingGeographicalDistributionRegions = payload || false
    },
    setPlanLoading(payload: any) {
      this.plan.loading = payload
    },
    setViewstate(payload: any) {
      this.viewState = payload
    },
    setMapupdateflags(payload: any) {
      this.mapUpdateFlags = { ...this.mapUpdateFlags, ...payload }
    },
    setProximityflags({
      key,
      id,
      value,
    }: {
      key: keyof ProximityFlags
      id: string
      value: unknown
    }) {
      // Vue 3 reactivity tracks plain property assignment (no Vue.set needed).
      this.proximityFlags[key][id] = value
    },
    resetProximityflags() {
      this.proximityFlags = {
        open: {},
        selected: {},
        halfSelected: {},
        hidden: {},
      }
    },
    // Accepts an optional payload (ignored) to preserve the former Vuex
    // mutation's permissive signature — callers pass the removed geometry ids.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeCustomGeometry(_payload?: unknown) {
      this.fixme = null
    },
    setOpenGeometry(id: any) {
      this.openCustomGeometry = id
    },
    setNewPoiDatasetForm(payload: any) {
      this.newPoiDatasetForm = {
        ...this.newPoiDatasetForm,
        ...payload,
      }
    },
    setDistribution(payload: any = {}) {
      this.distribution = payload
    },
    modifyPlanSubfilterTabname(
      payload: { id?: number | string; tabname?: string } = {},
    ) {
      this.plan.filters = [...this.plan.filters].map((item, index) => {
        if (index === payload.id) {
          item.tabname = payload.tabname
        }
        return item
      })
      this.plan_backup = structuredClone(deepToRaw(this.plan))
    },
    addSubplanFilters() {
      // add a new subplan filters
      this.plan.filters = [...this.plan.filters, MODEL_SUBPLAN_FILTERS()]
      // copy countries to the new subplan filters
      this.plan.filters[this.plan.filters.length - 1].countries = [
        ...this.plan.filters[0].countries,
      ]
      // copy proximity to the new subplan filters
      this.plan.filters[this.plan.filters.length - 1].proximity = {
        ...this.plan.filters[0].proximity,
      }
      // copy audience to the new subplan filters
      this.plan.filters[this.plan.filters.length - 1].delivery =
        this.plan.filters[0].delivery
      // copy geographical distribution to the new subplan filters
      if (this.plan.filters[0].geographical_distribution) {
        this.plan.filters[
          this.plan.filters.length - 1
        ].geographical_distribution = {
          ...this.plan.filters[0].geographical_distribution,
        }
      }
      // define active the new subplan filters
      this.activeSubPlanFiltersIndex = this.plan.filters.length - 1
      // Update dirty property
      this.plan.dirty = true
      // Update plan backup
      this.plan_backup = structuredClone(deepToRaw(this.plan))
    },
    setSubplanFiltersIndex(payload: any) {
      this.activeSubPlanFiltersIndex = payload
    },
    removeSubplanFilters(payload: any) {
      if (this.plan.filters.length > 1) {
        const filters = [...this.plan.filters].filter(
          (item, index) => index !== payload,
        )
        this.plan.filters = [...filters]
        this.plan.dirty = true
        this.plan_backup = structuredClone(deepToRaw(this.plan))
      }
    },
    updateProximityData(payload: any) {
      this.plan.proximity = payload
      // Update POIs filter cache when proximity data changes.
      updatePOIsFilterCache(
        this.plan as unknown as Parameters<typeof updatePOIsFilterCache>[0],
      )
    },
    updateLegendFilter(payload: any) {
      this.plan.legend = {
        ...this.plan.legend,
        ...payload,
      }
    },
    setAssetsFinishedLoad(payload: any) {
      this.assetsFinishedLoad = payload
    },
    setExcludedAssetLayerAvailable(payload: any) {
      this.plan.legend.canAssetsExcludedLayerBeShown = payload
    },
    setAssetsDetails(payload: any) {
      this.assetsDetails = payload
    },
    setDefaultUserCountries(payload: any) {
      this.defaultUserCountries = payload
    },
    setAssetsReadyWithAllProperties(payload: any) {
      this.assetsReadyWithAllProperties = payload.ready
    },
    setAssetsFacingOrientationIcons(payload: any) {
      this.plan.legend.facingOrientationIcons = payload
    },
    setPoisVisibility(payload: any) {
      this.plan.legend.poisVisibility = payload
    },
    setPoisIconsVisibility(payload: boolean) {
      this.plan.legend.poisIconsVisibility = payload
    },
    setBoundariesVisibility(payload: any) {
      this.plan.legend.boundariesVisibility = payload
    },
    setAudiencesQuintileVisibility(payload: any) {
      this.plan.legend.audiencesQuintile = payload
    },
    setAssetsIdsLayerVisibility(payload: any) {
      this.plan.legend.assetsIdsLayerVisibility = payload
    },
    setColorizeAssets(payload: any) {
      this.plan.legend.colorizeAssets = payload
    },
    setPoisBufferVisibility(payload: any) {
      this.plan.legend.poisBufferVisibility = payload
    },
    setHighlightedAssets(payload: any) {
      this.highlightedAssets = payload
    },
    setForceLogout(payload: any) {
      this.forceLogout = payload
    },
    setPoiFreeColor(payload: any) {
      const poisColors = { ...this.poisColors }
      const colorForPOI = poisColors[payload]
      if (colorForPOI === undefined) {
        let assignedColor = POIsOthersColor
        const assignedColors = Object.values(
          JSON.parse(JSON.stringify(this.poisColors)),
        )
        const availableColors = POIsRampColor.filter((color) => {
          return !assignedColors.find((assignedColorsElement) =>
            areEquals(
              assignedColorsElement as number[],
              color as unknown as number[],
            ),
          )
        })
        if (availableColors.length > 0) {
          assignedColor = availableColors[0]
        }
        poisColors[payload] = assignedColor
        this.poisColors = poisColors
      }
    },
    clearPoiColor(payload: any) {
      const poisColors = { ...this.poisColors }
      const colorToBeRemoved = poisColors[payload]
      if (colorToBeRemoved === undefined) {
        throw new Error(`POI ${payload} has no color associated`)
      }

      delete poisColors[payload]
      this.poisColors = poisColors
    },
    clearPoisColor() {
      this.poisColors = {}
    },
    setPoiIcon(payload: { poiKey: string; fileName: string }) {
      // A replacement keeps the fit already picked for the selection.
      this.poisIcons = {
        ...this.poisIcons,
        [payload.poiKey]: {
          fileName: payload.fileName,
          fit: this.poisIcons[payload.poiKey]?.fit ?? DEFAULT_POI_ICON_FIT,
        },
      }
    },
    setPoiIconFit(payload: { poiKey: string; fit: Poi.PoiIconFit }) {
      const assignment = this.poisIcons[payload.poiKey]
      if (!assignment) {
        return
      }
      this.poisIcons = {
        ...this.poisIcons,
        [payload.poiKey]: { ...assignment, fit: payload.fit },
      }
    },
    clearPoiIcon(payload: string) {
      if (this.poisIcons[payload] === undefined) {
        return
      }
      const poisIcons = { ...this.poisIcons }
      delete poisIcons[payload]
      this.poisIcons = poisIcons
    },
    clearPoisIcons() {
      this.poisIcons = {}
    },
    setPlanDirty() {
      this.plan.dirty = true
    },
    setBasemap(payload: any) {
      this.plan.basemap = payload
    },
    setAssetsAngleNorthMap(payload: any) {
      this.plan.legend.angleNorthMap = payload
    },
    setHeatmapAssetsVisibility(payload: any) {
      this.plan.legend.heatmapAssetsVisibility = payload
    },
    setHeatmapAssetsType(payload: any) {
      this.plan.legend.heatmapAssetsType = payload
    },
    setAssetsDigitalStaticProperties(payload: any) {
      this.assetsProperties = payload
    },
    addGeoboundaryGeometryToCache(payload: any) {
      console.debug('🌐 Saving to geoboundaries cache:', payload)
      const found = this.geoBoundariesGeometriesCache.find(
        (element) =>
          element.id === payload.id && element.type_id === payload.type_id,
      )
      if (found) return
      if (this.geoBoundariesGeometriesCache.length < 20) {
        this.geoBoundariesGeometriesCache.push(payload)
      } else {
        this.geoBoundariesGeometriesCache.shift()
        this.geoBoundariesGeometriesCache.push(payload)
      }
      console.debug(
        '🌐 Cache content: Geoboundaries geometries',
        this.geoBoundariesGeometriesCache.map((g) => `${g.id} (${g.type_id})`),
      )
    },
    setMyPlans(payload: any) {
      this.plans.user = payload
    },
    // Collision: former ACTIONS.SET_PUBLIC_PLANS was a trivial
    // `commit(MUTATIONS.SET_PUBLIC_PLANS, payload)` wrapper, so it collapses
    // 1:1 into the mutation body below.
    setPublicPlans(payload: any) {
      this.plans.public = payload
    },
    setPlansClients(payload: any) {
      this.plans.clients = payload
    },
    setIsSubscribedToPlans(payload: any) {
      this.plans.subscribed = payload
    },
    setClustersHidden(payload: any) {
      this.plan.legend.clustersHidden = payload
    },

    // ─── Former Vuex actions (async, now camelCase) ─────────────────────────
    async loadPlan({
      id,
      sharedPlan,
      password,
    }: {
      id: string
      sharedPlan?: { advanceOptions?: unknown }
      password?: string
    }) {
      this.setPlanLoading(true)
      const originalUrl = window.location.href

      try {
        const defaultPlan = MODEL_PLAN()
        const defaultSubPlanFilters = MODEL_SUBPLAN_FILTERS()
        let plan: LoadablePlan = MODEL_PLAN()
        if (sharedPlan) {
          console.debug('🟪 Shared Plan: Fetching plan properties...')
          const planFetched = await sharedPlanService.getPlan(
            APP_METADATA,
            BrowserEnvironmentResolver.getInstance(),
            id,
            password,
          )

          plan = { ...plan, ...planFetched } as unknown as LoadablePlan

          plan = prepareLegendInPlan(
            plan as unknown as Plan.Plan,
            sharedPlan.advanceOptions as Parameters<
              typeof prepareLegendInPlan
            >[1],
          ) as unknown as LoadablePlan

          if (typeof plan.filters === 'string') {
            plan.filters = deserializeFilters(plan.filters)
            plan.created_at =
              (plan.created_at as { _seconds: number })._seconds * 1000
            plan.modified_at =
              ((plan.modified_at as { _seconds: number } | undefined)
                ?._seconds as number) * 1000
          }

          /**
           * TODO: I have no idea why do not have a deserializer for proximity filter params
           * In case no proximity filter is set we need to do this for shared plans
           **/
          for (const filterTabs of plan.filters as SubPlanFilter[]) {
            if (!filterTabs.proximity) {
              filterTabs.proximity = { data: [] }
            }
          }
        } else {
          console.debug('🟪 Plan: Fetching...')
          plan = (await PlanService.getPlan(
            APP_METADATA,
            BrowserEnvironmentResolver.getInstance(),
            id,
          )) as unknown as LoadablePlan

          console.debug('🟪 Plan: Preparing filters...')
          const deserializedFilters = deserializeFilters(plan.filters as string)
          const filters = deserializedFilters.map((subfilter: Filter) => {
            return { ...defaultSubPlanFilters, ...subfilter }
          })
          plan.filters = filters as unknown as SubPlanFilter[]

          console.debug('🟪 Plan: Preparing attributes...')
          const rawPoisColors = (plan.poisColors as string) ?? '{}'
          const poisColors = JSON.parse(rawPoisColors)
          plan.poisColors = poisColors

          const rawPoisIcons = (plan.poisIcons as string) ?? '{}'
          plan.poisIcons = JSON.parse(rawPoisIcons)
        }

        const preparedPlan = {
          ...defaultPlan,
          ...plan,
          dirty: false,
          created: plan.created_at,
          modified: plan.modified_at || plan.created_at,
          legend: deserializeLegend(plan.legend as Plan.Legend),
        }

        // load dirty plan (going back from insights panel)
        if (!sharedPlan) {
          const { filters, previousRoute } = getPlanFiltersFromStorage()
          if (
            previousRoute &&
            previousRoute.includes('/insights') &&
            previousRoute.includes('/panel') &&
            filters !== null
          ) {
            preparedPlan.filters = filters as unknown as SubPlanFilter[]
            preparedPlan.dirty = true
            console.debug(
              '🟪 Plan: Coming from Insights, reloading old dirty plan',
            )
          }
        }

        console.debug('🟪 Plan: Saving plan in the state...')
        this.savePlan(preparedPlan)
      } catch (err) {
        // eslint-disable-next-line
        console.error('[actions.js] Error in LOAD_PLAN', err)
        if (sharedPlan) {
          const currentUrl = window.location.href
          const parameters = currentUrl.split('?')[1]
          const sharedPlanFeedbackUrl = `/shared-plan-feedback?${parameters}&type=password&link=${currentUrl}`
          window.location.href = sharedPlanFeedbackUrl
        } else {
          const planNotFoundUrl = `/plan-not-found?link=${encodeURIComponent(
            originalUrl,
          )}`
          window.location.href = planNotFoundUrl
        }
      } finally {
        this.setPlanLoading(false)
      }
    },
    async loadCustomGeoboundary({ id }: { id: string }) {
      this.setPlanLoading(true)

      try {
        const defaultPlan = MODEL_PLAN()
        const defaultSubPlanFilters = MODEL_SUBPLAN_FILTERS()
        console.debug('      👉 Fetching custom geoboundary dataset')
        const dataset = (await CustomGeoboundary.getCustomGeoboundary(
          APP_METADATA,
          BrowserEnvironmentResolver.getInstance(),
          id,
        )) as unknown as LoadablePlan
        console.debug('         OK - Fetching custom geoboundary dataset')
        const deserializedFilters = deserializeFilters(
          dataset.filters as string,
        )
        const filters = deserializedFilters.map((subfilter: Filter) => {
          return { ...defaultSubPlanFilters, ...subfilter }
        })

        const preparedDataset = {
          ...defaultPlan,
          ...dataset,
          dirty: false,
          filters,
          created: dataset.created_at,
          modified: dataset.modified_at || dataset.created_at,
        }

        this.savePlan(preparedDataset)
      } catch (err) {
        // eslint-disable-next-line
        console.error('[actions.js] Error in LOAD_CUSTOM_GEOBOUNDARY', err)
        const errResponse = (err as { response?: { status?: number } }).response
        const fromAPI = errResponse && (errResponse.status ?? 0) >= 400
        window.alert(
          `${
            fromAPI ? 'API' : ''
          } Error loading plan. Check the browser console for more details`,
        )
      } finally {
        this.setPlanLoading(false)
      }
    },
    async createPlan(payload: {
      filters: unknown
      legend: unknown
      [key: string]: unknown
    }) {
      const planFormatted = {
        ...payload,
        filters: JSON.stringify(payload.filters),
        legend: serializeLegend(payload.legend as Plan.Legend),
      }
      const id = await PlanService.createPlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        planFormatted as unknown as Plan.Plan,
      )
      const auth0Instance = getAuth0ServiceIntance()
      const userId = auth0Instance.getUser()
      const plan = { ...payload, created_by: userId, id, modified: new Date() }
      this.savePlan({
        ...plan,
        dirty: false,
        filters: this.plan.filters as unknown as typeof plan.filters,
      })
      const { categories, brands, locations, tags } = getPOIsByTypesFromFilter(
        this.plan.filters[this.activeSubPlanFiltersIndex].proximity
          .data as Parameters<typeof getPOIsByTypesFromFilter>[0],
      )
      this.updateProximityData({
        categories,
        brands,
        locations,
        tags,
      })
      return plan
    },
    async createCustomGeoboundary(payload: {
      dataset: { filters: unknown; [key: string]: unknown }
      refresh?: boolean
    }) {
      const datasetFormatted = {
        ...payload.dataset,
        filters: JSON.stringify(payload.dataset.filters),
      }
      const userId = getAuth0ServiceIntance().getUser() as string
      const id = await CustomGeoboundary.createCustomGeoboundary(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        userId,
        datasetFormatted as unknown as Plan.Plan,
      )
      const dataset = { ...payload.dataset, id }
      if (!payload.refresh) {
        return dataset
      }
      this.savePlan({
        ...dataset,
        dirty: false,
        filters: this.plan.filters,
      })
      return dataset
    },
    async updatePlan(payload: {
      id: string
      plan: {
        filters: unknown
        legend: unknown
        poisColors?: unknown
        poisIcons?: unknown
        [key: string]: unknown
      }
    }) {
      const plan = {
        ...payload.plan,
        filters: JSON.stringify(payload.plan.filters),
        legend: serializeLegend(payload.plan.legend as Plan.Legend),
      }

      if (typeof plan.poisColors !== 'string') {
        if (!plan.poisColors) {
          plan.poisColors = {}
        }
        plan.poisColors = JSON.stringify(plan.poisColors)
      }

      if (typeof plan.poisIcons !== 'string') {
        plan.poisIcons = JSON.stringify(plan.poisIcons ?? {})
      }

      await PlanService.updatePlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        payload.id,
        plan as unknown as Plan.Plan,
      )
      this.savePlan({
        ...plan,
        modified: new Date(),
        dirty: false,
        filters: this.plan.filters,
      })
      const { categories, brands, locations, tags } = getPOIsByTypesFromFilter(
        this.plan.filters[this.activeSubPlanFiltersIndex].proximity
          .data as Parameters<typeof getPOIsByTypesFromFilter>[0],
      )
      this.updateProximityData({
        categories,
        brands,
        locations,
        tags,
      })
    },
    async updateCustomGeoboundary(payload: {
      id: string
      plan: { filters: unknown; [key: string]: unknown }
    }) {
      const dataset = {
        ...payload.plan,
        filters: JSON.stringify(payload.plan.filters),
      }
      await CustomGeoboundary.updateCustomGeoboundary(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        payload.id,
        dataset as unknown as Plan.Plan,
      )
      this.savePlan({
        ...dataset,
        dirty: false,
        filters: this.plan.filters,
      })
    },
    async deletePlan({ id }: { id: string }) {
      return PlanService.deletePlan(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        id,
      )
    },
    async deleteCustomGeoboundary({ id }: { id: string }) {
      return CustomGeoboundary.deleteCustomGeoboundary(
        APP_METADATA,
        BrowserEnvironmentResolver.getInstance(),
        id,
      )
    },
    async loadPoisLayer() {
      try {
        this.setMapupdateflags({ pois: false })
      } catch (err) {
        console.error('[actions.js] Error in LOAD_POIS_LAYER', err)
        const errResponse = (err as { response?: { status?: number } }).response
        const fromAPI = errResponse && (errResponse.status ?? 0) >= 400
        window.alert(
          `${
            fromAPI ? 'API' : ''
          } Error updating POIs layer. Check the browser console for more details`,
        )
      }
    },
    async loadAssets(
      params: Record<string, unknown> = {
        sql: true,
        meta: true,
        results: true,
        page_size: 30,
        order_by: '-impressions',
      },
      replaceCount = true,
    ) {
      const assets = (await assetsService.getAssets({
        ...params,
        count: undefined,
      })) as { count?: number; [key: string]: unknown }

      if (!replaceCount) {
        const currentCount = this.getAssetsCount
        assets.count = currentCount
      }
      this.setAssets(assets)
      return assets
    },
    async loadExcludeAssets(
      params: Record<string, unknown> = {
        sql: true,
        meta: false,
        results: false,
      },
    ) {
      const assets = await assetsService.getAssets({
        ...params,
        exclude_action: true,
      })
      this.setAssetsExcluded(assets)
      return assets
    },
    async updateAssetsInitialStats(params: Record<string, unknown> = {}) {
      this.setAssetsInitialStats(params)
    },
    async updateAssetLayer(params: Record<string, unknown> = {}) {
      let needToGetSecondaryPropertiesForAssets = false
      const plan = this.getPlan
      if (params.doNotEmitBusEvent === undefined) {
        params.doNotEmitBusEvent = false
      }

      const flyToExtent = params.flyToExtent ?? true

      this.setAssetsLoading(true)

      let allAssets: AssetFeature[] = []

      const retiresForWaittingIndexDdInitialization = 5
      const timeBetweenRetries = [0, 100, 200, 300, 400]
      let retries = 0
      let successfullyFetchedAssets = false

      // Getting assets

      if (!params.sharedPlan) {
        while (
          retries < retiresForWaittingIndexDdInitialization &&
          !successfullyFetchedAssets
        ) {
          await sleep(timeBetweenRetries[retries])
          try {
            console.debug(
              `🟪 Plan: 🔁 Fetching Assets: Attempt: ${
                retries + 1
              } || Assets: ${allAssets.length}`,
            )
            if (allAssets.length === 0) {
              const { allGeoJsonAssets, needToGetSecondaryProperties } =
                await AssetsService.fetchLayerV2(
                  getAppMetadata(),
                  BrowserEnvironmentResolver.getInstance(),
                  plan.filters as unknown as Filter[],
                )
              needToGetSecondaryPropertiesForAssets =
                needToGetSecondaryProperties
              console.debug(
                `🟪 Plan: 🐹 Secondary assets properties needed: ${needToGetSecondaryProperties}`,
              )
              allAssets = allGeoJsonAssets.data
                .features as unknown as AssetFeature[]
              this.setAssetsReadyWithAllProperties({
                ready: !needToGetSecondaryProperties,
              })
            }
            successfullyFetchedAssets = true
          } catch (err) {
            console.error(
              '   🔴 Vuex - actions.js - UPDATE_ASSET_LAYER - An error occur while trying to fetch assets',
              err,
            )
            retries++
            successfullyFetchedAssets = false
          }
        }
        if (!successfullyFetchedAssets) {
          console.warn(
            '🔴 🔴 Could not set assets into indexBD, seems indexDB is not ready ... 🔴 🔴',
          )
          this.setMapupdateflags({ assets: false })
          this.setAssetsLoading(false)
          return
        }
        if (retries > 2) {
          console.warn(
            `🟠 Took ${
              retries + 1
            } attempts to correctly fetch assets, it would be a good idea to take a look why`,
          )
        }
        console.debug(
          `🟪 Plan: 🔁 Fetched Assets: Attempt: ${retries + 1} || Assets: ${
            allAssets.length
          }`,
        )
      } else {
        const assetsLyr = layerServiceC3.getLayer(ASSETS)
        // getLayer may return a [] sentinel; the asset layer carries data on state
        const sharedAssets = (assetsLyr as unknown as DeckLayerLike | undefined)
          ?.state?.data
        // Remove excluded attribute from all assets for shared plans
        allAssets = Array.isArray(sharedAssets)
          ? sharedAssets.map((asset) => {
              if (asset?.properties?.excluded !== undefined) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { excluded, ...restProperties } = asset.properties
                return { ...asset, properties: restProperties }
              }
              return asset
            })
          : []
      }

      const generateAuditEvent =
        params.generateAuditEvent === undefined
          ? true
          : params.generateAuditEvent
      console.debug('📢 Audit: Generating Event', generateAuditEvent)
      console.debug('🟪 Plan: 🔍 Filtering assets...')
      let assetsToShow = await filterAssetsFromPlan(
        allAssets as unknown as Parameters<typeof filterAssetsFromPlan>[0],
        plan as unknown as Parameters<typeof filterAssetsFromPlan>[1],
        generateAuditEvent as Parameters<typeof filterAssetsFromPlan>[2],
      )

      // Update assets count and impressions
      const impressions = getImpressionsForAssets(assetsToShow)
      this.setAssetsTotalStats({
        count: assetsToShow.filter((asset) => !asset.properties.excluded)
          .length,
        impressions,
      })

      const activeSubPlanFiltersIndex = this.getSubplanFiltersIndex
      const currentSubFilter = plan.filters[activeSubPlanFiltersIndex]

      // manage layers visibility
      manageBoundariesLayerVisibility(
        plan.legend as unknown as Parameters<
          typeof manageBoundariesLayerVisibility
        >[0],
        false,
      )
      managePoisLayerVisibility(
        plan.legend as unknown as Parameters<
          typeof managePoisLayerVisibility
        >[0],
        currentSubFilter.proximity as unknown as Parameters<
          typeof managePoisLayerVisibility
        >[1],
        false,
      )
      layerServiceC3.updateDeckInstance()

      // Update proximity data
      const { categories, brands, locations, tags } = getPOIsByTypesFromFilter(
        currentSubFilter.proximity.data as Parameters<
          typeof getPOIsByTypesFromFilter
        >[0],
      )
      this.updateProximityData({
        categories,
        brands,
        locations,
        tags,
      })

      this.setExcludedAssetLayerAvailable(
        assetsToShow.length < allAssets.length,
      )
      const mapZoom = layerServiceC3.getZoom()
      const declusteringZoomLevel = getZoomLevelForDeclustering(APP_METADATA)
      const assetSimpleLayerShouldBeVisible = shouldAssetSimpleLayerBeVisible(
        declusteringZoomLevel,
        mapZoom,
      )
      const assetsLayerVisibility = assetsToShow.length > 0

      const secondaryPropertiesReady = this.getAssetsReadyWithAllProperties
      console.debug('       All assets properties are ready now? ', {
        needToGetSecondaryPropertiesForAssets,
        secondaryPropertiesReady,
      })
      if (needToGetSecondaryPropertiesForAssets && secondaryPropertiesReady) {
        const { allGeoJsonAssets } = await AssetsService.fetchLayerV2(
          getAppMetadata(),
          BrowserEnvironmentResolver.getInstance(),
          plan.filters as unknown as Filter[],
        )
        allAssets = allGeoJsonAssets.data.features as unknown as AssetFeature[]

        const assetsMap = new Map<unknown, AssetFeature>()
        allAssets.forEach((asset) => {
          assetsMap.set(asset.properties.id, asset)
        })
        const assetsToShowFullProperties = assetsToShow.map((asset) => {
          return assetsMap.get((asset as unknown as AssetFeature).properties.id)
        })
        assetsToShow =
          assetsToShowFullProperties as unknown as typeof assetsToShow
        console.debug('        Re-fetched assets properties')
      }

      const isAudiencesQuintileFeatureEnabled =
        isAudiencesQuintileEnabled(APP_METADATA)
      const isAudiencesQuintileEnableInLegend =
        this.getAudiencesQuintileVisibility
      const geoboundaryFiltersAreSetOK =
        isGeoboundaryFilterReadyForAudiencesQuintile(
          plan.filters[0] as unknown as Parameters<
            typeof isGeoboundaryFilterReadyForAudiencesQuintile
          >[0],
        )
      const audiencesQuintileFiltersAreSetOK = isAudiencesQuintileInFilter(
        plan.filters[0] as unknown as Parameters<
          typeof isAudiencesQuintileInFilter
        >[0],
      )
      if (
        isAudiencesQuintileFeatureEnabled &&
        isAudiencesQuintileEnableInLegend &&
        geoboundaryFiltersAreSetOK &&
        audiencesQuintileFiltersAreSetOK
      ) {
        setupAudiencesQuintileLayer(
          APP_METADATA,
          BrowserEnvironmentResolver.getInstance(),
          plan as unknown as Parameters<typeof setupAudiencesQuintileLayer>[2],
          !!params.sharedPlan,
        )
      }

      layerServiceC3.updateLayer(ASSETS, {
        ...params,
        data: allAssets,
        assetsToShow,
        visible: assetsLayerVisibility && assetSimpleLayerShouldBeVisible,
        flyToExtent,
        secondaryPropertiesReady,
      })
      layerServiceC3.updateLayer(CLUSTER_ASSETS, {
        ...params,
        data: allAssets,
        assetsToShow,
        visible: assetsLayerVisibility && !assetSimpleLayerShouldBeVisible,
        flyToExtent,
        secondaryPropertiesReady,
      })
      if (isFeatureLayerHeatMapEnabled(APP_METADATA)) {
        const heatmapVisibility =
          assetsLayerVisibility && this.getHeatmapAssetsVisibility
        layerServiceC3.updateLayer(HEATMAP_ASSETS_LAYER, {
          ...params,
          assetsToShow,
          visible: heatmapVisibility,
          heatmapType: this.getHeatmapAssetsType,
        })
      }
      // Legend
      const legend = this.getLegendFilter
      if (legend.excludeAssets) {
        const assetsExcluded = getUnfilteredElements(
          allAssets as unknown as Parameters<typeof getUnfilteredElements>[0],
          assetsToShow,
        )
        layerServiceC3.updateLayer(EXCLUDED_CLUSTER_ASSETS, {
          assetsToShow: assetsExcluded,
          ...params,
          visible: !assetSimpleLayerShouldBeVisible,
        })
        layerServiceC3.updateLayer(EXCLUDED_ASSETS, {
          assetsToShow: assetsExcluded,
          ...params,
          visible: assetSimpleLayerShouldBeVisible,
        })
      } else {
        layerServiceC3.hideLayer(EXCLUDED_ASSETS)
        layerServiceC3.hideLayer(EXCLUDED_CLUSTER_ASSETS)
      }

      this.setMapupdateflags({ assets: false })
      this.setAssetsLoading(false)

      // When this redraw does not fly the camera (e.g. excluding/including
      // assets), deck.gl 9's idle AnimationLoop never draws a frame on its own,
      // so the layer changes above stay unrendered (stale icons). A flyTo drives
      // its own frames, so only force a redraw on the no-flyTo path.
      if (!flyToExtent) {
        layerServiceC3.forceIdleRedraw()
      }

      if (isIntelligentMediaTypeDigitalStaticEnabled(APP_METADATA)) {
        const assetsProperties = analyzeAssetsForAllDigitalOrAllClassic(
          assetsToShow as unknown as Parameters<
            typeof analyzeAssetsForAllDigitalOrAllClassic
          >[0],
        )
        this.setAssetsDigitalStaticProperties(assetsProperties)
      }

      console.debug('🟢 Updating Assets Layer - OK')
    },
    updateAssetsFacingOrientationIcons(enabled: boolean) {
      this.setAssetsFacingOrientationIcons(enabled)
    },
    updatePoisBufferVisibility(enabled: boolean) {
      this.setPoisBufferVisibility(enabled)
    },
    updatePoisVisibility(enabled: boolean) {
      this.setPoisVisibility(enabled)
    },
    updatePoisIconsVisibility(enabled: boolean) {
      this.setPoisIconsVisibility(enabled)
    },
    updateBoundariesVisibility(enabled: boolean) {
      this.setBoundariesVisibility(enabled)
    },
    updateAudiencesQuintileVisibility(enabled: boolean) {
      this.setAudiencesQuintileVisibility(enabled)
    },
    updateAssetsIdsLayerVisibility(enabled: boolean) {
      this.setAssetsIdsLayerVisibility(enabled)
    },
    updateColorizeAssets(enabled: boolean) {
      this.setColorizeAssets(enabled)
    },
    async updatePlanFilter(payload: {
      filters: unknown
      flagToSetDirty?: string
    }) {
      /*
      Payload:
         - filters: Array of filters
         - flagToSetDirty?: must be ['assets', 'pois', 'geoboundaries']
      */

      const filtersUpdated = payload.filters
      if (!filtersUpdated) {
        throw new Error('Filters are required')
      }
      if (!Array.isArray(filtersUpdated)) {
        throw new Error('Filters must be an array')
      }
      if (filtersUpdated.length === 0) {
        throw new Error('Filters cannot be empty')
      }

      const validFlags = ['assets', 'pois', 'geoboundaries']
      const flagToSetDirty = payload.flagToSetDirty
      if (flagToSetDirty && !validFlags.includes(flagToSetDirty)) {
        throw new Error('Invalid flag to set dirty, available values are: ')
      }

      this.savePlanFilter({
        filters: filtersUpdated,
      })

      if (flagToSetDirty) {
        this.setMapupdateflags({ [flagToSetDirty]: true })
      }
    },
    async updateClustersHidden(payload: boolean) {
      this.setClustersHidden(payload)
    },
  },
})

export { MODULE_NAME as MODULE_NAME_PLAN }
